import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getGraphQLClient,
  GET_BOOK_COMPANION_CHAT,
  SEND_COMPANION_MESSAGE,
  APPLY_COMPANION_PROPOSAL,
  DISMISS_COMPANION_PROPOSAL,
  REVERT_COMPANION_PROPOSAL,
  START_COMPANION_CHAT,
} from '../lib/graphql';

export type ProposalStatus = 'pending' | 'applied' | 'dismissed' | 'reverted';

export interface CompanionProposal {
  id: string;
  kind: string;
  status: ProposalStatus;
  title: string;
  rationale: string | null;
  // Computed blast radius — shape varies by kind (see ImpactAnalyzer).
  impact: Record<string, unknown>;
  structural: boolean;
  revertable: boolean;
  createdAt: string;
  appliedAt: string | null;
  revertedAt: string | null;
}

export interface CompanionMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string | null;
  createdAt: string;
  proposals: CompanionProposal[];
}

export interface CompanionChat {
  id: string;
  createdAt: string;
  messages: CompanionMessage[];
  pendingProposals: CompanionProposal[];
  appliedProposals: CompanionProposal[];
}

// Surface the companion was opened from. Each focus is a separate chat thread
// (server-side page_context) so the plan-page and reader conversations stay
// distinct. Omit for the default thread.
export type CompanionFocus = 'plan' | 'reader';

const key = (bookId: string | undefined, focus?: CompanionFocus) => [
  'bookCompanion',
  bookId,
  focus ?? null,
];

export function useBookCompanionChat(
  bookId: string | undefined,
  focus?: CompanionFocus,
  enabled = true
) {
  return useQuery({
    queryKey: key(bookId, focus),
    queryFn: async () => {
      const data = await getGraphQLClient().request<{ bookCompanionChat: CompanionChat | null }>(
        GET_BOOK_COMPANION_CHAT,
        { bookId, focus }
      );
      return data.bookCompanionChat;
    },
    enabled: !!bookId && enabled,
  });
}

export function useSendCompanionMessage(bookId: string | undefined, focus?: CompanionFocus) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (content: string) => {
      const data = await getGraphQLClient().request<{
        sendCompanionMessage: {
          success: boolean;
          errors: string[];
          message: CompanionMessage | null;
        };
      }>(SEND_COMPANION_MESSAGE, { bookId, content, focus });
      return data.sendCompanionMessage;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(bookId, focus) }),
  });
}

// Applying/reverting a proposal mutates book state, so the reader view must
// refetch too — callers pass an onApplied to invalidate the reader query.
export function useApplyCompanionProposal(
  bookId: string | undefined,
  focus?: CompanionFocus,
  onApplied?: () => void
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const data = await getGraphQLClient().request<{
        applyCompanionProposal: {
          success: boolean;
          errors: string[];
          proposal: CompanionProposal | null;
        };
      }>(APPLY_COMPANION_PROPOSAL, { proposalId });
      return data.applyCompanionProposal;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(bookId, focus) });
      onApplied?.();
    },
  });
}

export function useDismissCompanionProposal(bookId: string | undefined, focus?: CompanionFocus) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const data = await getGraphQLClient().request<{
        dismissCompanionProposal: { success: boolean; errors: string[] };
      }>(DISMISS_COMPANION_PROPOSAL, { proposalId });
      return data.dismissCompanionProposal;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(bookId, focus) }),
  });
}

export function useRevertCompanionProposal(
  bookId: string | undefined,
  focus?: CompanionFocus,
  onReverted?: () => void
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (proposalId: string) => {
      const data = await getGraphQLClient().request<{
        revertCompanionProposal: {
          success: boolean;
          errors: string[];
          proposal: CompanionProposal | null;
        };
      }>(REVERT_COMPANION_PROPOSAL, { proposalId });
      return data.revertCompanionProposal;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: key(bookId, focus) });
      onReverted?.();
    },
  });
}

export function useStartCompanionChat(bookId: string | undefined, focus?: CompanionFocus) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const data = await getGraphQLClient().request<{
        startCompanionChat: { success: boolean; errors: string[] };
      }>(START_COMPANION_CHAT, { bookId, focus });
      return data.startCompanionChat;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: key(bookId, focus) }),
  });
}
